<template>
  <div class="field-mappings-tab">
    <div class="tab-header mb-6">
      <h3 class="text-lg font-semibold text-gray-900">Field Mappings</h3>
      <p class="text-sm text-gray-600 mt-1">
        Map data from your campaigns to Salesforce fields
      </p>
    </div>

    <!-- Template Section (Enhanced) -->
    <div v-if="templates.length > 0" class="template-section mb-6">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-start space-x-3">
          <i class="fas fa-magic text-blue-500 mt-1"></i>
          <div class="flex-1">
            <h4 class="text-sm font-medium text-blue-800 mb-2">Quick Setup with Templates</h4>
            <p class="text-xs text-blue-600 mb-3">
              Start with a pre-configured template to automatically set up common field mappings
            </p>
            
            <div class="flex items-center space-x-3">
              <select 
                v-model="selectedTemplateId"
                class="form-select flex-1 template-select"
                @change="onTemplateSelect"
              >
                <option value="">Choose a template...</option>
                <option 
                  v-for="template in templates" 
                  :key="template.id"
                  :value="template.id"
                >
                  {{ template.name }}
                </option>
              </select>
              <button
                v-if="selectedTemplateId"
                @click="applyTemplate"
                class="btn btn-primary whitespace-nowrap"
              >
                Apply Template
              </button>
            </div>
            
            <div v-if="selectedTemplate" class="mt-2 text-xs text-blue-600">
              {{ selectedTemplate.description }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lead Object Notice -->
    <div class="object-notice mb-6">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-center space-x-3">
          <i class="fas fa-user-plus text-blue-600"></i>
          <div>
            <h4 class="text-sm font-medium text-blue-900">Lead Field Mappings</h4>
            <p class="text-xs text-blue-800 mt-1">
              All campaigns are synced as Leads in Salesforce. Configure how your campaign data maps to Lead fields below.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Search and Filter -->
    <div class="search-section mb-4">
      <div class="flex items-center space-x-3">
        <div class="relative flex-1">
          <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Search field mappings..."
            class="form-input pl-10 w-full"
          >
        </div>
        <button
          @click="showAllMappings = !showAllMappings"
          class="btn btn-outline whitespace-nowrap"
        >
          <i class="fas fa-eye mr-1"></i>
          {{ showAllMappings ? 'Hide Empty' : 'Show All' }}
        </button>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="quick-actions mb-6">
      <div class="flex flex-wrap items-center gap-2">
        <button
          @click="addMapping"
          class="btn btn-primary"
        >
          <i class="fas fa-plus mr-1"></i>
          Add Mapping
        </button>
        
        <button
          @click="addCommonMappings"
          class="btn btn-secondary"
        >
          <i class="fas fa-bolt mr-1"></i>
          Add Common Fields
        </button>
        
        <button
          v-if="filteredMappings.length > 0"
          @click="clearAllMappings"
          class="btn btn-outline text-red-600 border-red-200 hover:bg-red-50"
        >
          <i class="fas fa-trash mr-1"></i>
          Clear All
        </button>
      </div>
    </div>

    <!-- Mappings List -->
    <div class="mappings-container">
      <div v-if="filteredMappings.length === 0" class="empty-state">
        <div class="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <i class="fas fa-exchange-alt text-gray-400 text-3xl mb-4"></i>
          <h4 class="text-lg font-medium text-gray-600 mb-2">No Field Mappings</h4>
          <p class="text-sm text-gray-500 mb-4">
            Create field mappings to sync campaign data to Salesforce
          </p>
          <button @click="addMapping" class="btn btn-primary">
            <i class="fas fa-plus mr-1"></i>
            Add Your First Mapping
          </button>
        </div>
      </div>

      <div v-else class="space-y-3">
        <!-- Mobile-friendly mapping cards with drag and drop -->
        <draggable 
          v-model="mappings"
          :item-key="(item) => item.id || mappings.indexOf(item)"
          handle=".drag-handle"
          :animation="200"
          class="space-y-3"
          @end="emitUpdate"
        >
          <template #item="{ element: mapping, index }">
            <div
              class="mapping-card bg-white border rounded-lg p-4 hover:border-blue-300 transition-colors"
            >
          <!-- Desktop Layout -->
          <div class="hidden md:flex items-start space-x-4">
            <!-- Drag Handle -->
            <div class="drag-handle cursor-move text-gray-400 hover:text-gray-600 mt-6">
              <i class="fas fa-grip-vertical"></i>
            </div>

            <!-- Mapping Type -->
            <div class="mr-4" style="width: 7rem;">
              <label class="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select 
                v-model="mapping.mapping_type"
                class="form-select-sm mapping-type-select"
                :data-mapping-id="mapping.id"
                @change="onMappingTypeChange(mapping)"
              >
                <option value="direct">Direct</option>
                <option value="hardcoded">Static</option>
                <option value="computed">Computed</option>
              </select>
            </div>

            <!-- Source Configuration -->
            <div class="flex-1 mr-2">
              <label class="block text-xs font-medium text-gray-500 mb-1">ERP Field (Source)</label>
              <div v-if="mapping.mapping_type === 'direct'">
                <select 
                  v-model="mapping.source_field"
                  :key="`source-${mapping.id}`"
                  class="form-select-sm w-full source-field-select"
                  :data-mapping-id="mapping.id"
                >
                  <option value="">Select source field...</option>
                  <optgroup label="Campaign Fields">
                    <option value="name">Campaign Name</option>
                    <option value="campaign_type.name">Campaign Type</option>
                    <option value="goal">Goal Amount</option>
                    <option value="event_date">Event Date</option>
                    <option value="business_name">Business Name</option>
                  </optgroup>
                  <optgroup label="User Fields">
                    <option value="user.first_name">User First Name</option>
                    <option value="user.last_name">User Last Name</option>
                    <option value="user.email">User Email</option>
                    <option value="user.address">User Address</option>
                    <option value="user.city">User City</option>
                    <option value="user.state">User State</option>
                    <option value="user.zip">User Zip</option>
                  </optgroup>
                </select>
                <div v-if="mapping.source_field" class="text-xs text-gray-500 italic mt-1">
                  {{ getFieldPreview(mapping.source_field, 'direct') }}
                </div>
              </div>
              <div v-else-if="mapping.mapping_type === 'hardcoded'">
                <input 
                  v-model="mapping.mapping_value"
                  type="text"
                  class="form-input-sm w-full"
                  placeholder="Enter static value..."
                >
                <div class="text-xs text-gray-500 italic mt-1">
                  {{ mapping.mapping_value ? `Value: "${mapping.mapping_value}"` : getFieldPreview('', 'hardcoded') }}
                </div>
              </div>
              <div v-else-if="mapping.mapping_type === 'computed'">
                <select 
                  v-model="mapping.mapping_value"
                  :key="`computed-${mapping.id}`"
                  class="form-select-sm w-full computed-value-select"
                  :data-mapping-id="mapping.id"
                >
                  <option value="">Select computation...</option>
                  <option value="campaign_url">Campaign URL</option>
                  <option value="campaign_id">Campaign ID</option>
                  <option value="foundation_name">Foundation Name</option>
                  <option value="parent_campaign_name">Parent Campaign Name</option>
                  <option value="full_address">Full Address</option>
                </select>
                <div v-if="mapping.mapping_value" class="text-xs text-gray-500 italic mt-1">
                  {{ getFieldPreview(mapping.mapping_value, 'computed') }}
                </div>
              </div>
            </div>

            <!-- Arrow -->
            <div class="text-gray-400 mt-8 px-2">
              <i class="fas fa-arrow-right"></i>
            </div>

            <!-- Target Field -->
            <div class="w-64">
              <label class="block text-xs font-medium text-gray-500 mb-1">Salesforce Field (Target)</label>
              <select 
                v-model="mapping.target_field"
                :ref="`targetField_${mapping.id}`"
                class="form-select-sm w-full salesforce-field-select"
                :data-mapping-id="mapping.id"
              >
                <option value="">Select Salesforce field...</option>
                <option 
                  v-for="(label, field) in getSalesforceFields()" 
                  :key="field"
                  :value="field"
                >
                  {{ label }} ({{ field }})
                </option>
              </select>
              <div v-if="mapping.target_field" class="text-xs text-gray-500 mt-1">
                {{ getSalesforceFieldDescription(mapping.target_field) }}
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center space-x-2 mt-6">
              <button 
                @click="moveMapping(mapping, -1)"
                :disabled="mappings.indexOf(mapping) === 0"
                class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                title="Move up"
              >
                <i class="fas fa-chevron-up"></i>
              </button>
              <button 
                @click="moveMapping(mapping, 1)"
                :disabled="mappings.indexOf(mapping) === mappings.length - 1"
                class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                title="Move down"
              >
                <i class="fas fa-chevron-down"></i>
              </button>
              <button 
                @click="removeMapping(mapping)"
                class="p-1 text-red-500 hover:text-red-700"
                title="Delete"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>

          <!-- Mobile Layout -->
          <div class="md:hidden space-y-3">
            <div class="flex items-center justify-between">
              <h5 class="font-medium text-gray-800">Mapping {{ index + 1 }}</h5>
              <div class="flex items-center space-x-2">
                <button 
                  @click="moveMapping(mapping, -1)"
                  :disabled="mappings.indexOf(mapping) === 0"
                  class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <i class="fas fa-chevron-up"></i>
                </button>
                <button 
                  @click="moveMapping(mapping, 1)"
                  :disabled="mappings.indexOf(mapping) === mappings.length - 1"
                  class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <i class="fas fa-chevron-down"></i>
                </button>
                <button 
                  @click="removeMapping(mapping)"
                  class="p-1 text-red-500 hover:text-red-700"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">Type</label>
                <select 
                  v-model="mapping.mapping_type"
                  class="form-select-sm w-full mapping-type-select-mobile"
                  :data-mapping-id="mapping.id"
                  @change="onMappingTypeChange(mapping)"
                >
                  <option value="direct">Direct</option>
                  <option value="hardcoded">Static</option>
                  <option value="computed">Computed</option>
                </select>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">ERP Field (Source)</label>
                <div v-if="mapping.mapping_type === 'direct'">
                  <select 
                    v-model="mapping.source_field"
                    :key="`mobile-source-${mapping.id}`"
                    class="form-select-sm w-full source-field-select-mobile"
                    :data-mapping-id="mapping.id"
                  >
                    <option value="">Select source field...</option>
                    <optgroup label="Campaign Fields">
                      <option value="name">Campaign Name</option>
                      <option value="campaign_type.name">Campaign Type</option>
                      <option value="goal">Goal Amount</option>
                      <option value="event_date">Event Date</option>
                      <option value="business_name">Business Name</option>
                    </optgroup>
                    <optgroup label="User Fields">
                      <option value="user.first_name">User First Name</option>
                      <option value="user.last_name">User Last Name</option>
                      <option value="user.email">User Email</option>
                      <option value="user.address">User Address</option>
                      <option value="user.city">User City</option>
                      <option value="user.state">User State</option>
                      <option value="user.zip">User Zip</option>
                    </optgroup>
                  </select>
                  <div v-if="mapping.source_field" class="text-xs text-gray-500 italic mt-1">
                    {{ getFieldPreview(mapping.source_field, 'direct') }}
                  </div>
                </div>
                <div v-else-if="mapping.mapping_type === 'hardcoded'">
                  <input 
                    v-model="mapping.mapping_value"
                    type="text"
                    class="form-input-sm w-full"
                    placeholder="Enter static value..."
                  >
                  <div class="text-xs text-gray-500 italic mt-1">
                    {{ mapping.mapping_value ? `Value: "${mapping.mapping_value}"` : getFieldPreview('', 'hardcoded') }}
                  </div>
                </div>
                <div v-else-if="mapping.mapping_type === 'computed'">
                  <select 
                    v-model="mapping.mapping_value"
                    :key="`mobile-computed-${mapping.id}`"
                    class="form-select-sm w-full computed-value-select-mobile"
                    :data-mapping-id="mapping.id"
                  >
                    <option value="">Select computation...</option>
                    <option value="campaign_url">Campaign URL</option>
                    <option value="campaign_id">Campaign ID</option>
                    <option value="foundation_name">Foundation Name</option>
                    <option value="parent_campaign_name">Parent Campaign Name</option>
                    <option value="full_address">Full Address</option>
                  </select>
                  <div v-if="mapping.mapping_value" class="text-xs text-gray-500 italic mt-1">
                    {{ getFieldPreview(mapping.mapping_value, 'computed') }}
                  </div>
                </div>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">Salesforce Field (Target)</label>
                <select 
                  v-model="mapping.target_field"
                  :ref="`mobileTargetField_${mapping.id}`"
                  class="form-select-sm w-full salesforce-field-select-mobile"
                  :data-mapping-id="mapping.id"
                >
                  <option value="">Select Salesforce field...</option>
                  <option 
                    v-for="(label, field) in getSalesforceFields()" 
                    :key="field"
                    :value="field"
                  >
                    {{ label }} ({{ field }})
                  </option>
                </select>
                <div v-if="mapping.target_field" class="text-xs text-gray-500 mt-1">
                  {{ getSalesforceFieldDescription(mapping.target_field) }}
                </div>
              </div>
            </div>
          </div>
            </div>
          </template>
        </draggable>
      </div>
    </div>

    <!-- Mapping Summary -->
    <div v-if="mappings.length > 0" class="mapping-summary mt-6">
      <div class="bg-gray-50 rounded-lg p-4">
        <h4 class="text-sm font-medium text-gray-800 mb-3">
          <i class="fas fa-chart-pie text-gray-600 mr-2"></i>
          Mapping Summary
        </h4>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div class="text-center">
            <div class="text-lg font-semibold text-blue-600">{{ mappings.length }}</div>
            <div class="text-gray-600">Total Mappings</div>
          </div>
          <div class="text-center">
            <div class="text-lg font-semibold text-green-600">{{ completedMappings }}</div>
            <div class="text-gray-600">Completed</div>
          </div>
          <div class="text-center">
            <div class="text-lg font-semibold text-purple-600">{{ directMappings }}</div>
            <div class="text-gray-600">Direct</div>
          </div>
          <div class="text-center">
            <div class="text-lg font-semibold text-orange-600">{{ staticMappings }}</div>
            <div class="text-gray-600">Static</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick, onUnmounted } from 'vue';
import draggable from 'vuedraggable';
import $ from 'jquery';
import 'select2';
import 'select2/dist/css/select2.min.css';

export default {
  name: 'FieldMappingsTab',
  components: {
    draggable,
  },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    templates: {
      type: Array,
      default: () => [],
    },
    selectedTemplate: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const mappings = ref([]);
    const targetObject = ref('Lead');
    const selectedTemplateId = ref('');
    const searchTerm = ref('');
    const showAllMappings = ref(false);

    // Salesforce field schemas
    const leadFields = {
      // Standard Fields
      'LastName': 'Last Name',
      'FirstName': 'First Name',
      'Salutation': 'Salutation',
      'Company': 'Company',
      'Title': 'Title',
      'Email': 'Email',
      'Phone': 'Phone',
      'MobilePhone': 'Mobile',
      'Fax': 'Fax',
      'Street': 'Street',
      'City': 'City',
      'State': 'State/Province',
      'PostalCode': 'Postal Code',
      'Country': 'Country',
      'LeadSource': 'Lead Source',
      'Status': 'Pipeline Status',
      'Industry': 'Industry',
      'Rating': 'Rating',
      'AnnualRevenue': 'Annual Revenue',
      'NumberOfEmployees': 'No. of Employees',
      'Website': 'Website',
      'Description': 'Description',
      'DoNotCall': 'Do Not Call',
      'HasOptedOutOfEmail': 'Email Opt Out',
      'HasOptedOutOfFax': 'Fax Opt Out',
      'GenderIdentity': 'Gender Identity',
      'Pronouns': 'Pronouns',
      'LastTransferDate': 'Last Transfer Date',
      'IndividualId': 'Individual',
      // Custom Fields
      'Account_Name_Org_Name__c': 'Account Name (Org Name)',
      'Address__c': 'Account Address',
      'Lead_Type_Formula__c': 'Account Status (L)',
      'Type__c': 'Account Type at Creation',
      'Advocacy_Score__c': 'Advocacy Score',
      'Advocacy_Scores__c': 'Advocacy Scores',
      'Calendly__CalendlyCreated__c': 'CalendlyCreated',
      'Came_From_Typeform__c': 'Came From Typeform',
      'Campaign_Type__c': 'Campaign Type',
      'Campaign_Program_Name__c': 'Side-Out Program Name',
      'Consecutive_Years_Formula__c': 'Consecutive Won Years Formula',
      'Created_Date__c': 'Created Date',
      'Date_Lead_moved_to_Kick_Off__c': 'Date Lead moved to Kick Off',
      'Dig_Pink_Campaign_ID__c': 'Campaign ID',
      'Dig_Pink_Campaign_Name__c': 'Supporter Campaign',
      'Dig_Pink_Parent_Campaign_Name__c': 'Foundation Campaign',
      'Event_Date__c': 'Event Date',
      'Fundraising_Page__c': 'Fundraising Page',
      'Goal_Amount__c': 'Goal Amount',
      'Industry_From_Account__c': 'Industry from Account',
      'Kick_Off_Call_Completed__c': 'Kick Off Call Completed',
      'Lead_Category__c': 'Lead Category',
      'Lead_Confidence_Level__c': 'Lead Confidence Level',
      'Lead_External_ID__c': 'Lead External ID',
      'Lead_Fiscal_Year__c': 'Lead Fiscal Year',
      'Lead_Forecast_Amount__c': 'Lead Forecast Amount',
      'Lead_Name__c': 'Lead Name',
      'Lead_Name_Hyperlinked__c': 'Lead Name Hyperlinked',
      'Lead_Source_Formula__c': 'Lead Source Formula',
      'Lost_Reason__c': 'Lost Reason',
      'Marked_for_Review__c': 'Marked for Review',
      'New_ERP_Lead__c': 'New ERP Lead',
      'Number_of_Activities__c': 'Number of Activities',
      'Opportunity_Advocacy_Score__c': 'Opportunity Advocacy Scores',
      'Opportunity_Advocacy_Total__c': 'Opportunity Advocacy Total',
      'Other_Contacts__c': 'Other Contacts',
      'Participated_in_Side_Out_Program_Before__c': 'Participated in Side-Out Program Before?',
      'Primary_Contact__c': 'Primary Contact',
      'Ready_for_Conversion__c': 'Ready for Conversion',
      'Reason_for_Review__c': 'Reason for Review',
      'Representation__c': 'Representation',
      'Role__c': 'Role',
      'Sent_Cards__c': 'Sent Cards',
      'State__c': 'State',
      'State_Reportfield__c': 'State Reportfield',
      'Territory__c': 'Territory',
      'Territory_Assignment_Complete__c': 'Territory Assignment Complete',
      'Typeform__c': 'Typeform',
      'typeform__Typeform_Form_Mapping__c': 'Typeform Form Mapping',
      'Updated_from_ERP__c': 'Updated from ERP',
      'Web_Form_Id__c': 'Web Form Id',
      'Web_Form_Recent_Completed_Date__c': 'Web Form Recent Completed Date',
      'Web_Form_Title__c': 'Web Form Title',
      'What_is_your_connection_to_breast_cancer__c': 'What is your connection to breast cancer',
      'Your_Dig_Pink_Participation_Interests__c': 'Your Dig Pink Participation Interests?',
      'ActiveCamp_ActiveCampaign_Sync_Status__c': 'Active Campaign Sync Field',
    };

    // Contact fields removed - we now only create Leads

    // Computed properties
    const selectedTemplate = computed(() => {
      if (!selectedTemplateId.value) return null;
      return props.templates.find(template => template.id === selectedTemplateId.value);
    });

    const getSalesforceFields = () => {
      // Always use Lead fields now
      const fields = leadFields;
      // Sort fields alphabetically by label and format with API name
      const sortedFields = {};
      Object.entries(fields)
        .sort((a, b) => a[1].localeCompare(b[1]))
        .forEach(([key, value]) => {
          sortedFields[key] = value;
        });
      return sortedFields;
    };
    
    const formatFieldOption = (apiName, label) => {
      return `${label} (${apiName})`;
    };
    
    // Initialize Select2 on all dropdowns
    const initializeSelect2 = async () => {
      await nextTick();
      
      // Initialize template selector
      $('.template-select').each(function() {
        const $select = $(this);
        
        // Skip if already initialized
        if ($select.hasClass('select2-hidden-accessible')) {
          return;
        }
        
        $select.select2({
          placeholder: 'Choose a template...',
          allowClear: true,
          width: '100%',
          minimumResultsForSearch: 5,
        });
        
        // Handle Select2 change event
        $select.on('select2:select select2:clear', function(e) {
          selectedTemplateId.value = $(this).val() || '';
          onTemplateSelect();
        });
      });
      
      // Initialize mapping type selects
      $('.mapping-type-select, .mapping-type-select-mobile').each(function() {
        const $select = $(this);
        const mappingId = $select.data('mapping-id');
        
        // Skip if already initialized
        if ($select.hasClass('select2-hidden-accessible')) {
          return;
        }
        
        $select.select2({
          placeholder: 'Select type...',
          width: '100%',
          minimumResultsForSearch: Infinity, // Disable search for small lists
        });
        
        // Handle Select2 change event
        $select.on('select2:select', function(e) {
          const value = $(this).val() || 'direct';
          const mapping = mappings.value.find(m => m.id === mappingId);
          if (mapping) {
            mapping.mapping_type = value;
            onMappingTypeChange(mapping);
          }
        });
      });
      
      // Initialize source field selects
      $('.source-field-select, .source-field-select-mobile').each(function() {
        const $select = $(this);
        const mappingId = $select.data('mapping-id');
        
        // Force destroy if already initialized to prevent duplicates
        if ($select.hasClass('select2-hidden-accessible')) {
          $select.select2('destroy');
        }
        
        $select.select2({
          placeholder: 'Select source field...',
          allowClear: true,
          width: '100%',
          minimumResultsForSearch: 0,
        });
        
        // Handle Select2 change event
        $select.on('select2:select select2:clear', function(e) {
          const value = $(this).val() || '';
          const mapping = mappings.value.find(m => m.id === mappingId);
          if (mapping) {
            mapping.source_field = value;
            emitUpdate();
          }
        });
      });
      
      // Initialize computed value selects
      $('.computed-value-select, .computed-value-select-mobile').each(function() {
        const $select = $(this);
        const mappingId = $select.data('mapping-id');
        
        // Skip if already initialized
        if ($select.hasClass('select2-hidden-accessible')) {
          return;
        }
        
        $select.select2({
          placeholder: 'Select computation...',
          allowClear: true,
          width: '100%',
          minimumResultsForSearch: Infinity, // Disable search for small lists
        });
        
        // Handle Select2 change event
        $select.on('select2:select select2:clear', function(e) {
          const value = $(this).val() || '';
          const mapping = mappings.value.find(m => m.id === mappingId);
          if (mapping) {
            mapping.mapping_value = value;
            emitUpdate();
          }
        });
      });
      
      // Initialize target field selects (existing code)
      $('.salesforce-field-select, .salesforce-field-select-mobile').each(function() {
        const $select = $(this);
        const mappingId = $select.data('mapping-id');
        
        // Skip if already initialized
        if ($select.hasClass('select2-hidden-accessible')) {
          return;
        }
        
        $select.select2({
          placeholder: 'Search and select Salesforce field...',
          allowClear: true,
          width: '100%',
          minimumResultsForSearch: 0,
        });
        
        // Handle Select2 change event
        $select.on('select2:select select2:clear', function(e) {
          const value = $(this).val() || '';
          const mapping = mappings.value.find(m => m.id === mappingId);
          if (mapping) {
            mapping.target_field = value;
            emitUpdate();
          }
        });
      });
    };
    
    // Destroy Select2 instances
    const destroySelect2 = () => {
      $('.template-select, .mapping-type-select, .mapping-type-select-mobile, .source-field-select, .source-field-select-mobile, .computed-value-select, .computed-value-select-mobile, .salesforce-field-select, .salesforce-field-select-mobile').each(function() {
        if ($(this).hasClass('select2-hidden-accessible')) {
          $(this).select2('destroy');
        }
      });
    };

    const filteredMappings = computed(() => {
      let filtered = mappings.value;

      // Always show Lead mappings (or mappings without target_object for backward compatibility)
      filtered = filtered.filter(mapping => 
        mapping.target_object === 'Lead' || !mapping.target_object
      );

      // Filter by search term
      if (searchTerm.value) {
        const term = searchTerm.value.toLowerCase();
        filtered = filtered.filter(mapping => {
          const sourceText = mapping.source_field?.toLowerCase() || '';
          const targetText = mapping.target_field?.toLowerCase() || '';
          const valueText = mapping.mapping_value?.toLowerCase() || '';
          return sourceText.includes(term) || targetText.includes(term) || valueText.includes(term);
        });
      }

      // Filter by show all toggle
      if (!showAllMappings.value) {
        filtered = filtered.filter(mapping => 
          mapping.target_field && (mapping.source_field || mapping.mapping_value)
        );
      }

      return filtered;
    });

    const completedMappings = computed(() => {
      return mappings.value.filter(mapping => 
        mapping.target_object === targetObject.value &&
        mapping.target_field && (mapping.source_field || mapping.mapping_value)
      ).length;
    });

    const directMappings = computed(() => {
      return mappings.value.filter(mapping => 
        mapping.target_object === targetObject.value &&
        mapping.mapping_type === 'direct'
      ).length;
    });

    const staticMappings = computed(() => {
      return mappings.value.filter(mapping => 
        mapping.target_object === targetObject.value &&
        mapping.mapping_type === 'hardcoded'
      ).length;
    });

    // Preview Examples
    const getFieldPreview = (fieldName, mappingType) => {
      if (mappingType === 'direct') {
        const examples = {
          'name': 'Example: "2024 Spring Volleyball Tournament"',
          'campaign_type.name': 'Example: "On Behalf of a School"',
          'goal': 'Example: "$10,000"',
          'event_date': 'Example: "2024-05-15"',
          'business_name': 'Example: "Lincoln High School"',
          'user.first_name': 'Example: "John"',
          'user.last_name': 'Example: "Smith"',
          'user.email': 'Example: "john.smith@example.com"',
          'user.address': 'Example: "123 Main St"',
          'user.city': 'Example: "San Diego"',
          'user.state': 'Example: "CA"',
          'user.zip': 'Example: "92101"',
        };
        return examples[fieldName] || '';
      } else if (mappingType === 'computed') {
        const examples = {
          'campaign_url': 'Example: "https://giving.side-out.org/campaign/12345"',
          'campaign_id': 'Example: "12345"',
          'foundation_name': 'Example: "Lincoln High School Volleyball"',
          'parent_campaign_name': 'Example: "2024 Dig Pink Campaign"',
          'full_address': 'Example: "123 Main St, San Diego, CA 92101"',
        };
        return examples[fieldName] || '';
      } else if (mappingType === 'hardcoded') {
        return 'Example: The exact value you enter above';
      }
      return '';
    };

    const getSalesforceFieldDescription = (fieldName) => {
      const descriptions = {
        // Lead fields
        'LastName': 'Required - Contact\'s last name',
        'FirstName': 'Contact\'s first name',
        'Company': 'Required - Organization or company name',
        'Email': 'Primary email address',
        'Phone': 'Primary phone number',
        'Street': 'Street address',
        'City': 'City name',
        'State': 'State or province code',
        'PostalCode': 'ZIP or postal code',
        'LeadSource': 'How this lead was generated',
        'Status': 'Lead status in pipeline',
        'Website': 'Organization website URL',
        'Description': 'Additional notes or details',
        // Custom fields
        'Goal_Amount__c': 'Fundraising goal in dollars',
        'Event_Date__c': 'Date of the event',
        'Campaign__c': 'Related campaign name',
        'Fundraising_Page__c': 'URL to fundraising page',
        'Campaign_Type__c': 'Type of campaign (e.g., Dig Pink)',
        // Contact fields
        'MailingStreet': 'Mailing street address',
        'MailingCity': 'Mailing city',
        'MailingState': 'Mailing state/province',
        'MailingPostalCode': 'Mailing ZIP/postal code',
        'Title': 'Job title or position',
        'Department': 'Department or division',
        'Birthdate': 'Date of birth',
      };
      return descriptions[fieldName] || '';
    };

    // Methods
    // No longer need setTargetObject since we're always using Lead

    const addMapping = () => {
      mappings.value.push({
        id: `temp_${Date.now()}_${Math.random()}`,
        mapping_type: 'direct',
        source_field: '',
        mapping_value: '',
        target_object: 'Lead', // Always Lead now
        target_field: '',
      });
      emitUpdate();
    };

    const addCommonMappings = () => {
      const commonMappings = [
        { source_field: 'user.first_name', target_field: 'FirstName' },
        { source_field: 'user.last_name', target_field: 'LastName' },
        { source_field: 'user.email', target_field: 'Email' },
        { source_field: 'user.city', target_field: 'City' }, // Always Lead fields
        { source_field: 'user.state', target_field: 'State' }, // Always Lead fields
      ];

      commonMappings.forEach(mapping => {
        const exists = mappings.value.some(m => m.target_field === mapping.target_field);
        if (!exists) {
          mappings.value.push({
            id: `temp_${Date.now()}_${Math.random()}`,
            mapping_type: 'direct',
            source_field: mapping.source_field,
            mapping_value: '',
            target_object: 'Lead', // Always Lead now
            target_field: mapping.target_field,
          });
        }
      });
      emitUpdate();
    };

    const removeMapping = (mapping) => {
      const index = mappings.value.findIndex(m => m.id === mapping.id);
      if (index !== -1) {
        mappings.value.splice(index, 1);
        emitUpdate();
      }
    };

    const moveMapping = (mapping, direction) => {
      const index = mappings.value.findIndex(m => m.id === mapping.id);
      const newIndex = index + direction;
      if (index !== -1 && newIndex >= 0 && newIndex < mappings.value.length) {
        const item = mappings.value.splice(index, 1)[0];
        mappings.value.splice(newIndex, 0, item);
        emitUpdate();
      }
    };

    const clearAllMappings = () => {
      if (confirm('Are you sure you want to clear all field mappings?')) {
        mappings.value = [];
        emitUpdate();
      }
    };

    const onMappingTypeChange = (mapping) => {
      if (mapping.mapping_type === 'direct') {
        mapping.mapping_value = '';
      } else if (mapping.mapping_type === 'hardcoded' || mapping.mapping_type === 'computed') {
        mapping.source_field = '';
      }
      emitUpdate();
    };

    const onTemplateSelect = () => {
      // Template selection handled by parent component
    };

    const applyTemplate = async () => {
      if (selectedTemplate.value && selectedTemplate.value.mappings) {
        // Parse mappings if they're a string, otherwise use as-is
        let templateMappings = selectedTemplate.value.mappings;
        if (typeof templateMappings === 'string') {
          try {
            templateMappings = JSON.parse(templateMappings);
          } catch (e) {
            console.error('Failed to parse template mappings:', e);
            return;
          }
        }
        
        // Normalize the mappings with proper structure
        mappings.value = templateMappings.map((mapping, index) => ({
          id: `temp_${Date.now()}_${index}_${Math.random()}`,
          mapping_type: mapping.mapping_type || 'direct',
          source_field: mapping.source_field || '',
          mapping_value: mapping.mapping_value || '',
          target_object: 'Lead', // Always set to Lead
          target_field: mapping.target_field || '',
          ...mapping // Preserve any additional properties
        }));
        
        // Emit the update
        emitUpdate();
        
        // Reinitialize Select2 after DOM updates
        await nextTick();
        destroySelect2();
        initializeSelect2();
        
        // Keep the template selected for user reference
        // Only clear if user selects another template or clicks clear
        // selectedTemplateId.value = '';
      }
    };

    const emitUpdate = () => {
      emit('update:modelValue', [...mappings.value]);
    };

    // Helper to normalize mapping data
    const normalizeMappings = (mappingsArray) => {
      return (mappingsArray || []).map(mapping => ({
        id: mapping.id || `temp_${Date.now()}_${Math.random()}`,
        mapping_type: mapping.mapping_type || 'direct',
        source_field: mapping.source_field || '',
        mapping_value: mapping.mapping_value || '',
        target_object: mapping.target_object || 'Lead',
        target_field: mapping.target_field || '',
        ...mapping // Preserve any additional properties
      }));
    };

    // Initialize
    onMounted(async () => {
      mappings.value = normalizeMappings(props.modelValue);
      // Don't override targetObject - keep it as 'Lead' by default
      // User can switch manually between Lead/Contact
      
      // Auto-select Default template if available and no mappings exist
      if (props.templates && props.templates.length > 0 && mappings.value.length === 0) {
        const defaultTemplate = props.templates.find(t => t.name === 'Default');
        if (defaultTemplate) {
          selectedTemplateId.value = defaultTemplate.id;
        }
      }
      
      await nextTick();
      initializeSelect2();
    });
    
    // Cleanup on unmount
    onUnmounted(() => {
      destroySelect2();
    });

    // Watch for prop changes
    watch(() => props.modelValue, async (newValue) => {
      mappings.value = normalizeMappings(newValue);
      // Don't override targetObject - let user control it
      await nextTick();
      destroySelect2();
      initializeSelect2();
    }, { deep: true });
    
    // Watch for templates to load and auto-select Default if needed
    watch(() => props.templates, (newTemplates) => {
      if (newTemplates && newTemplates.length > 0 && !selectedTemplateId.value && mappings.value.length === 0) {
        const defaultTemplate = newTemplates.find(t => t.name === 'Default');
        if (defaultTemplate) {
          selectedTemplateId.value = defaultTemplate.id;
        }
      }
    }, { immediate: true });
    
    // Re-initialize Select2 when mappings change
    watch(() => mappings.value.length, async () => {
      await nextTick();
      destroySelect2(); // Destroy existing instances first
      initializeSelect2();
    });

    return {
      mappings,
      targetObject,
      selectedTemplateId,
      selectedTemplate,
      searchTerm,
      showAllMappings,
      filteredMappings,
      completedMappings,
      directMappings,
      staticMappings,
      getSalesforceFields,
      addMapping,
      addCommonMappings,
      removeMapping,
      moveMapping,
      clearAllMappings,
      onMappingTypeChange,
      onTemplateSelect,
      applyTemplate,
      emitUpdate,
      getFieldPreview,
      getSalesforceFieldDescription,
      initializeSelect2,
      destroySelect2,
    };
  },
};
</script>

<style scoped>

.form-select,
.form-input {
  @apply block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500;
}

.form-select-sm,
.form-input-sm {
  @apply block w-full px-3 py-2 text-sm border border-gray-300 rounded-none shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500;
}

/* OVERRIDE Select2 defaults - MUST be 40px height with no radius */
.select2-container {
  width: 100% !important;
}

/* Override with maximum specificity */
.select2-container .select2-selection--single,
.select2-container--default .select2-selection--single,
.field-mappings-tab .select2-container .select2-selection--single,
.field-mappings-tab .select2-container--default .select2-selection--single {
  height: 40px !important; /* Exact 40px height */
  min-height: 40px !important;
  max-height: 40px !important;
  padding: 0 !important;
  font-size: 0.875rem !important;
  line-height: 40px !important; /* Match container height */
  border: 1px solid #d1d5db !important;
  border-radius: 0 !important; /* Squared corners - no rounding */
  background-color: white !important;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05) !important;
  box-sizing: border-box !important;
  display: block !important;
}

.select2-container .select2-selection__rendered,
.select2-container--default .select2-selection__rendered,
.select2-container--default .select2-selection--single .select2-selection__rendered,
.field-mappings-tab .select2-container--default .select2-selection--single .select2-selection__rendered {
  padding-left: 0.75rem !important;
  padding-right: 2rem !important;
  padding-top: 0.5rem !important; /* Adjusted for 40px height */
  padding-bottom: 0.5rem !important; /* Adjusted for 40px height */
  color: #111827 !important;
  font-size: 0.875rem !important;
  line-height: 20px !important; /* Fixed line height for text */
}

.select2-container .select2-selection__placeholder,
.select2-container--default .select2-selection__placeholder {
  color: #9ca3af !important;
  font-size: 0.875rem !important;
  padding-left: 0.75rem !important;
  padding-top: 0.5rem !important; /* Adjusted for 40px height */
}

.select2-container .select2-selection__arrow,
.select2-container--default .select2-selection__arrow {
  height: 40px !important; /* Exact 40px height */
  top: 0 !important;
  right: 0.25rem !important;
  width: 2rem !important;
}

.select2-container .select2-selection__arrow b {
  border-color: #6b7280 transparent transparent transparent;
  border-width: 5px 4px 0 4px;
  margin-left: -4px;
  margin-top: -2px;
}

.select2-container--open .select2-selection__arrow b {
  border-color: transparent transparent #6b7280 transparent;
  border-width: 0 4px 5px 4px;
}

.select2-container--focus .select2-selection--single,
.select2-container--open .select2-selection--single {
  border-color: #3b82f6 !important;
  outline: 2px solid transparent !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
  border-radius: 0 !important; /* Force squared even on focus */
}

/* Special styling for template selector (larger form-select) */
.template-select + .select2-container .select2-selection--single {
  height: 40px !important; /* Exact 40px height for consistency */
}

.select2-dropdown {
  border: 1px solid #d1d5db !important;
  border-radius: 0 !important; /* Squared corners for dropdown */
  font-size: 0.875rem !important;
}

.select2-search--dropdown .select2-search__field {
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
  border: 1px solid #d1d5db !important;
  border-radius: 0 !important; /* Squared search field */
}

.select2-results__option {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.select2-results__option--highlighted[aria-selected] {
  background-color: #eff6ff;
  color: #1e40af;
}

.select2-results__option[aria-selected=true] {
  background-color: #3b82f6;
  color: white;
}

.select2-selection__clear {
  margin-right: 1.5rem;
  color: #6b7280;
  font-size: 1rem;
  line-height: 1;
  padding: 0 0.25rem;
}

.select2-selection__clear:hover {
  color: #ef4444;
  background-color: #fee2e2;
  border-radius: 0.125rem;
}

/* ABSOLUTE FORCE - No border radius anywhere on Select2 */
.select2-container,
.select2-container *,
.select2-dropdown,
.select2-dropdown *,
.select2-container .select2-selection,
.select2-container .select2-selection--single,
.select2-container--default .select2-selection,
.select2-container--default .select2-selection--single,
.select2-container--open .select2-selection,
.select2-container--focus .select2-selection,
.select2-container--default *,
.select2-container--open * {
  border-radius: 0 !important;
}

/* Force exact height on all Select2 containers */
.select2-container .select2-selection,
.select2-container .select2-selection--single,
.select2-container .select2-selection--multiple {
  min-height: 40px !important; /* Exact 40px minimum */
  height: 40px !important; /* Force exact 40px */
  border-radius: 0 !important;
}

.btn {
  @apply px-4 py-2 rounded font-medium transition-colors;
}

.btn-primary {
  @apply bg-blue-500 text-white hover:bg-blue-600;
}

.btn-secondary {
  @apply bg-gray-500 text-white hover:bg-gray-600;
}

.btn-outline {
  @apply border border-gray-300 text-gray-700 hover:bg-gray-50;
}

.mapping-card {
  @apply transition-all duration-200;
}

.mapping-card:hover {
  @apply shadow-md;
}

.tab-header {
  @apply border-b border-gray-200 pb-4;
}

.drag-handle {
  @apply transition-colors duration-200;
}

@media (max-width: 640px) {
  .btn {
    @apply px-3 py-2 text-sm;
  }
  
  .quick-actions {
    @apply mb-4;
  }
  
  .quick-actions .btn {
    @apply flex-1 min-w-0;
  }
}
</style>

<!-- Force Select2 height override - this loads last -->
<style>
/* ABSOLUTE FINAL OVERRIDE - Must be 40px */
.field-mappings-tab .select2-container .select2-selection--single,
.field-mappings-tab .select2-container--default .select2-selection--single,
body .select2-container .select2-selection--single,
body .select2-container--default .select2-selection--single {
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  line-height: 40px !important;
  border-radius: 0 !important;
}

body .select2-container--default .select2-selection--single .select2-selection__rendered {
  line-height: 20px !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

/* Fix arrow height and positioning */
body .select2-container--default .select2-selection--single .select2-selection__arrow,
body .select2-container .select2-selection--single .select2-selection__arrow,
.field-mappings-tab .select2-container--default .select2-selection--single .select2-selection__arrow {
  height: 38px !important; /* 40px - 2px for borders */
  top: 0 !important;
  right: 0 !important;
  width: 30px !important;
  position: absolute !important;
}

/* Fix arrow icon positioning */
body .select2-container--default .select2-selection--single .select2-selection__arrow b {
  top: 50% !important;
  left: 50% !important;
  margin-left: -4px !important;
  margin-top: -2px !important;
}

/* Force all Select2 elements to be squared */
body .select2-container,
body .select2-container *,
body .select2-dropdown,
body .select2-dropdown * {
  border-radius: 0 !important;
}
</style>